import type {
  KitchenBoardView,
  PlanAssignment,
  PlanDecisions,
  PlanWarning,
  TaskStatus,
  TransferStatus,
} from "@kitchen/schemas";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api.js";

/** Shapes as the production controllers return them (apps/server/src/modules/production). */
export interface EngineTask {
  id: string;
  componentId: string;
  componentName: string;
  kitchenId: string;
  kitchenName: string;
  portions: number;
  cookDurationMinutes: number;
  startBy: string;
  readyBy: string;
  batches: number;
  batchCapacity?: number;
}
export interface EngineTransfer {
  componentId: string;
  componentName: string;
  fromKitchenId: string;
  fromKitchenName: string;
  toSiteId: string;
  toSiteName: string;
  portions: number;
  travelMinutes: number;
  departBy: string;
  arriveBy: string;
}
export interface Habit {
  componentId: string;
  componentName: string;
  suggestedKitchenId: string;
  suggestedKitchenName: string;
  countDays: number;
  totalDays: number;
  text: string;
}
export interface PlanView {
  businessDate: string;
  mealId: string;
  version: number | null;
  decisions: PlanDecisions | null;
  confirmedAt: string | null;
  mealTimesMissing: boolean;
  foods: {
    foodItemId: string;
    name: string;
    portions: number;
    components: { componentId: string; name: string; perPortionMilli: number }[];
  }[];
  components: { componentId: string; name: string; portions: number }[];
  kitchens: { siteId: string; name: string }[];
  output: { tasks: EngineTask[]; transfers: EngineTransfer[]; habits: Habit[] } | null;
  warnings: PlanWarning[];
  pendingPortions: number;
}
export interface KitchenProfileRow {
  siteId: string;
  siteName: string;
  active: boolean;
  version: number | null;
  dayStart: string;
  defaultCookMinutes: number;
  cookMinutes: Record<string, number>;
  potCapacity: Record<string, number>;
}
export interface TravelRow {
  fromSiteId: string;
  toSiteId: string;
  minutes: number;
}
export interface MealTimesRow {
  mealId: string;
  mealName: string;
  version: number | null;
  readyBy: string;
  handoverBy: string;
  packing: { siteId: string; packAt: string };
}
export interface SiteRow {
  id: string;
  name: string;
  kind: "kitchen" | "hall" | "serving-point";
  active: boolean;
}

export type { KitchenBoardView, PlanAssignment, PlanDecisions, TaskStatus, TransferStatus };

export const TASK_LABEL: Record<TaskStatus, string> = {
  planned: "در صف",
  started: "در دست پخت",
  ready: "آماده",
  cancelled: "لغو شد",
};
export const TRANSFER_LABEL: Record<TransferStatus, string> = {
  planned: "منتظر حرکت",
  sent: "در راه",
  received: "رسید",
  cancelled: "لغو شد",
};
export const COUNTDOWN_TONE = { done: "ok", green: "blue", yellow: "warn", red: "coral" } as const;

export const usePlan = (date: string, mealId: string) =>
  useQuery({
    queryKey: ["production-plan", date, mealId],
    queryFn: () => api<PlanView>(`/production/plan?date=${date}&mealId=${mealId}`),
    enabled: Boolean(mealId),
  });
export const useBoard = (date: string, mealId: string, siteId: string | null) =>
  useQuery({
    queryKey: ["production-board", date, mealId, siteId],
    queryFn: () =>
      api<KitchenBoardView>(
        `/production/board?date=${date}&mealId=${mealId}${siteId ? `&siteId=${siteId}` : ""}`,
      ),
    enabled: Boolean(mealId),
    refetchInterval: 30_000,
  });
export const useSites = () => useQuery({ queryKey: ["sites"], queryFn: () => api<SiteRow[]>("/sites") });
export const useProfiles = () =>
  useQuery({
    queryKey: ["production-kitchens"],
    queryFn: () => api<KitchenProfileRow[]>("/production/settings/kitchens"),
  });
export const useTravel = () =>
  useQuery({
    queryKey: ["production-travel"],
    queryFn: () => api<TravelRow[]>("/production/settings/travel"),
  });
export const useMealTimes = () =>
  useQuery({
    queryKey: ["production-meals"],
    queryFn: () => api<MealTimesRow[]>("/production/settings/meals"),
  });

/** "HH:mm" → minutes, for comparing and sorting times. */
export const minutesOf = (t: string) => {
  const [h, m] = t.split(":").map(Number) as [number, number];
  return h * 60 + m;
};
